import { ReactElement } from 'react';
import { IconButton } from '@cbhq/cds-web/buttons';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { Icon } from '@cbhq/cds-web/icons';
import { TableCell } from '@cbhq/cds-web/tables';
import { TextHeadline } from '@cbhq/cds-web/typography';

import { OrganizationUser, ProjectUserDetailsNEW } from ':cloud/types/service_proto';
import Permission from ':cloud/widgets/auth';

interface ActionCellProps {
  teamMember: ProjectUserDetailsNEW;
  signedInUser: boolean;
  handleMemberAction: (event: string, teamMember: ProjectUserDetailsNEW) => void;
}

function OwnerActions({ teamMember, handleMemberAction }) {
  return (
    <Permission permission="ROLE_OWNER">
      <ActionOption handleMemberAction={handleMemberAction} teamMember={teamMember}>
        {teamMember.status === 'invited' ? (
          <>
            <SelectOption value="resend-invitation" title="Resend invitation" />
            <SelectOption
              value="cancel-invitation"
              title={
                <TextHeadline color="negative" as="span">
                  Cancel invitation
                </TextHeadline>
              }
            />
          </>
        ) : (
          <>
            <SelectOption value="edit-member" title="Edit role" />
            <SelectOption
              value="remove-member"
              title={
                <TextHeadline color="negative" as="span">
                  Remove
                </TextHeadline>
              }
            />
          </>
        )}
      </ActionOption>
    </Permission>
  );
}

function AdminActions({ teamMember, handleMemberAction }) {
  return (
    <Permission permission="ROLE_ADMIN">
      <ActionOption handleMemberAction={handleMemberAction} teamMember={teamMember}>
        {teamMember.status === 'invited' ? (
          <>
            <SelectOption value="resend-invitation" title="Resend invitation" />
            <SelectOption
              value="cancel-invitation"
              title={
                <TextHeadline color="negative" as="span">
                  Cancel invitation
                </TextHeadline>
              }
            />
          </>
        ) : (
          <>
            <SelectOption value="edit-member" title="Edit role" />
            <SelectOption
              value="remove-member"
              title={
                <TextHeadline color="negative" as="span">
                  Remove
                </TextHeadline>
              }
            />
          </>
        )}
      </ActionOption>
    </Permission>
  );
}

export function ActionCell({ teamMember, signedInUser, handleMemberAction }: ActionCellProps) {
  if (signedInUser || teamMember.role === 'OWNER') {
    return (
      <TableCell alignItems="flex-end">
        <DashIcon />
      </TableCell>
    );
  }

  return (
    <TableCell alignItems="flex-end">
      <OwnerActions teamMember={teamMember} handleMemberAction={handleMemberAction} />
      <AdminActions teamMember={teamMember} handleMemberAction={handleMemberAction} />
    </TableCell>
  );
}

type ActionOptionProps = {
  handleMemberAction: (event: string, teamMember: OrganizationUser) => void;
  teamMember: OrganizationUser;
  children: ReactElement | ReactElement[] | null;
};

function ActionOption({ teamMember, children, handleMemberAction }: ActionOptionProps) {
  return (
    <Dropdown
      width={50}
      onChange={(event) => handleMemberAction(event, teamMember)}
      content={children}
    >
      <IconButton
        name="more"
        accessibilityLabel="Settings action"
        variant="secondary"
        transparent
      />
    </Dropdown>
  );
}

function DashIcon() {
  return <Icon name="horizontalLine" size="xs" color="foregroundMuted" spacingEnd={2} />;
}
